import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as core from '../../core'

import type { Employee as BaseEmployee } from './employee'
import { Functionalization } from './functionalization'
import type { GlAccount } from './gl-account'
import type { Payroll as BasePayroll } from './payroll'
import type { Project } from './project'
import type { SurveyStatus } from './survey-status'

export type Organization = core.common.Identified<Organization.Id> &
  core.common.Named &
  core.common.Described.Optionally &
  core.common.Coded & {
    entityId: core.dms.models.Entity.Id
    parentId?: Organization.Id
    jointUseInfo?: Organization.JointUseInfo
  }

export namespace Organization {
  export type Id = core.dms.data.Record.Id
  export namespace Id {
    export const isId = core.dms.data.Record.Id.isId
    export const assertIsId: TypeAsserter<Id> = TypeAsserter.Factory.from(isId)
  }

  export type Reference = core.dms.data.Value.Reference<
    core.dms.data.Value.Reference.Format.Entity,
    core.dms.models.Entity.Id
  >
  export namespace Reference {
    export const isReference: TypeChecker<Reference> =
      TypeChecker.Factory.object({
        entityId: core.dms.models.Entity.Id.isId,
        recordId: Organization.Id.isId,
      })
    export const assertIsReference: TypeAsserter<Reference> =
      TypeAsserter.Factory.from(isReference)
  }

  export interface Filter {
    includeInaccessible?: boolean
    parentIds?: readonly Organization.Id[]
    jointUseCalcMode?: JointUseCalculationMode
    ids?: readonly Organization.Id[]
  }

  export interface JointUseInfo {
    calculationMode: Organization.JointUseCalculationMode
    manualFunctionalizations?: readonly Functionalization[]
  }
  export type UpdateJointUse = JointUseInfo

  // Needs to be before namespace is defined
  export enum JointUseCalculationMode {
    Manual = 'Manual',
    Inherit = 'Inherit',
    Salary = 'Salary',
  }

  // Needs to be before namespace is defined
  const CALCULATION_MODES: ReadonlySet<JointUseCalculationMode> = new Set(
    Object.values(JointUseCalculationMode) as JointUseCalculationMode[],
  )
  export namespace JointUseCalculationMode {
    export const ALL: readonly JointUseCalculationMode[] =
      Array.from(CALCULATION_MODES)
    export const isJointUseCalculationMode =
      TypeChecker.Factory.oneOf(CALCULATION_MODES)
    export const assertIsJointUseCalculationMode: TypeAsserter<JointUseCalculationMode> =
      TypeAsserter.Factory.from(isJointUseCalculationMode)

    /** Checks if the mode is manual */
    export function isManual(mode: JointUseCalculationMode): boolean {
      return mode === JointUseCalculationMode.Manual
    }
    /** Checks if the mode is inherit */
    export function isInherit(mode: JointUseCalculationMode): boolean {
      return mode === JointUseCalculationMode.Inherit
    }
    /** Checks if the mode is salary */
    export function isSalary(mode: JointUseCalculationMode): boolean {
      return mode === JointUseCalculationMode.Salary
    }
  }

  /** Type guards and assertions for {@link UpdateJointUse} */
  export namespace UpdateJointUse {
    export const isUpdateJointUse: TypeChecker<UpdateJointUse> =
      TypeChecker.Factory.object<UpdateJointUse>({
        calculationMode: JointUseCalculationMode.isJointUseCalculationMode,
        manualFunctionalizations: TypeChecker.Factory.optional(
          TypeChecker.Factory.list(Functionalization.isFunctionalization),
        ),
      })
    export const assertIsUpdateJointUse: TypeAsserter<UpdateJointUse> =
      TypeAsserter.Factory.from(isUpdateJointUse)
  }

  export type Lookup = globalThis.Record<
    core.dms.models.Entity.Id,
    Organization.Id
  >
  export namespace Lookup {
    export const isLookup: TypeChecker<Lookup> = TypeChecker.Factory.record(
      core.dms.models.Entity.Id.isId,
      core.dms.data.Record.Id.isId,
    )
    export const assertIsLookup: TypeAsserter<Lookup> =
      TypeAsserter.Factory.from(isLookup)

    export type DetailsLookup = globalThis.Record<
      core.dms.models.Entity.Id,
      core.common.Identified<Organization.Id> &
        core.common.Named &
        core.common.Described.Optionally &
        core.common.Coded
    >

    /** The {@link DetailsLookup} of many organizations, keyed by organization id */
    export type WithDetails = globalThis.Record<Organization.Id, DetailsLookup>

    export type SurveyProgressLookup = globalThis.Record<
      SurveyStatus,
      { count: number; percent: number }
    >

    export type WithSurveyProgress = globalThis.Record<
      Organization.Id,
      SurveyProgressLookup
    >
  }

  export type WithPayrolls = Pick<
    Organization,
    'id' | 'code' | 'name' | 'description' | 'entityId'
  > & {
    payrolls: WithPayrolls.Payroll[]
  }
  export namespace WithPayrolls {
    export type Payroll = Organization.Payroll & {
      comment: string
      project?: Project.WithPI & Project.WithSponsor
      isOnCampus: boolean
      idcRate: number
    }
  }

  export type WithPayedEmployees = Pick<
    Organization,
    'id' | 'code' | 'name' | 'description' | 'entityId'
  > & {
    employees: WithPayedEmployees.Employee[]
  }
  export namespace WithPayedEmployees {
    export type Employee = Pick<
      BaseEmployee,
      'id' | 'code' | 'name' | 'description'
    > & {
      payrolls: Payroll[]
    }
    export type Payroll = Organization.Payroll & {
      percentOfPay: number
      comment: string
      project?: Project
    }
  }

  export interface Payroll {
    id: Payroll.Id
    amount: number
    /** The share of the amount charged to each of its IDC categories */
    idcAllocations: BasePayroll.IdcAllocation[]
    glAccount: GlAccount
    organizationLookup: Organization.Lookup.DetailsLookup
    percentOfPay: number
  }
  export namespace Payroll {
    export type Id = core.dms.data.Record.Id
    export const isId = core.dms.data.Record.Id.isId
    export const assertIsId: TypeAsserter<Id> = TypeAsserter.Factory.from(isId)
  }
}
